import React from 'react';
import i18n from 'i18next';
import { css } from '@emotion/core';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import { receiptSubTypes, receiptTypes } from 'editor/game_purchaseReward/utils/invoiceType';
import VerifyStatusLabel from 'client/components/verify/VerifyStatusLabel';
import usePromoSettingThemeStyle from 'hooks/useThemeStyleIntegration';
import { gameTypes } from 'config/promoGameTypes';
import { setTextVariantStyle } from 'utils/variantStyle';
import FormInfoButton from 'client/components/Result/EntriesInfo/form/FormInfoButton';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { VERIFY_MECHANISM_TYPES } from 'config/promoQualify';

export default function ResultInfoList({
    resultInfo = {},
    validStatus,
    gameType,
    formIds,
    entryUuid,
    showFormBtn
}) {
    const themeStyle = usePromoSettingThemeStyle();
    const { receiptType, receiptSubtype, receiptNumber = '', receiptPublishedAt } = resultInfo;
    const displayReceiptPublishedAt = Boolean(receiptPublishedAt)
        ? dayjs(receiptPublishedAt).format('YYYY-MM-DD')
        : '';
    const { enableVerifyMechanism, verifyMechanism } = useSelector(s => s.settingPromo);
    const shouldShowStatusLabel = !(
        enableVerifyMechanism && verifyMechanism === VERIFY_MECHANISM_TYPES.VERIFY_BY_PASS
    );

    const title = (() => {
        const upperCaseReceiptNumber = String(receiptNumber).toUpperCase();
        switch (gameType) {
            case gameTypes.PURCHASE_REWARD:
                let type = '';
                switch (Number(receiptType)) {
                    case receiptTypes.EINVOICE:
                        type = i18n.t(
                            receiptSubtype === receiptSubTypes.CARRIER
                                ? 'label.result.title.einvoice_carrier'
                                : 'label.result.title.einvoice'
                        );
                        break;
                    case receiptTypes.INVOICE:
                        type = i18n.t('label.result.title.invoice');
                        break;
                    case receiptTypes.RECEIPT:
                        type = i18n.t('label.result.title.receipt');
                        break;
                    default:
                        break;
                }
                return `${type} - ${upperCaseReceiptNumber}`;
            case gameTypes.ORDER_REWARD:
                return `${i18n.t('label.result.title.order_number')}：${upperCaseReceiptNumber}`;
            default:
                break;
        }
    })();

    if (gameType === gameTypes.PURCHASE_REWARD) {
        return (
            <>
                <div css={containerStyle}>
                    <div css={wrapperStyle}>
                        <Typography variant="subtitle2" css={textStyle(themeStyle?.titleColor)}>
                            {title}
                        </Typography>
                        {shouldShowStatusLabel && <VerifyStatusLabel status={validStatus} />}
                    </div>
                    <div css={wrapperStyle}>
                        <Typography variant="subtitle2" css={textStyle(themeStyle?.titleColor)}>
                            {i18n.t('label.purchase_reward.purchase_date_text')}：
                            {displayReceiptPublishedAt}
                        </Typography>
                        {showFormBtn && <FormInfoButton formIds={formIds} entryUuid={entryUuid} />}
                    </div>
                </div>
                <Divider css={purchaseRewardDividerStyle} />
            </>
        );
    }

    return (
        <>
            <div css={wrapperStyle}>
                <Typography variant="subtitle1" css={titleWrapperStyle(themeStyle.titleColor)}>
                    {title}
                </Typography>
                <div css={statusStyle}>
                    <VerifyStatusLabel status={validStatus} />
                </div>
            </div>
            <Divider css={dividerStyle} />
        </>
    );
}

const wrapperStyle = css`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const titleWrapperStyle = color => css`
    margin-top: 16px;
    color: ${color};
`;

const statusStyle = css`
    max-width: 89px;
    margin-top: 20px;
`;

const dividerStyle = css`
    margin: 16px -6px;
`;

const purchaseRewardDividerStyle = css`
    margin-bottom: 4px;
`;

const containerStyle = css`
    padding: 8px 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const textStyle = color => css`
    ${setTextVariantStyle('subtitle2')}
    color: ${color};
`;
